/** * タイムラインコンポーネント */
<template>
  <div class="box time-line">
    <loading-mask v-bind:isLoading="isLoading">
      <header class="title-bar">
        <h1 v-t="'appTimeline.timeline'" />
        <div class="title-bar-action">
          <div class="title-bar-reload">
            <p class="label">
              {{ $t('appTimeline.listUpdate.field') }} :
              {{ timeLastUpdated }}
            </p>
            <button-reload v-on:click="$_updateTimeline" />
          </div>
        </div>
      </header>
      <article class="box-content">
        <transition name="expand">
          <nav v-if="isNavigationBarRequired" class="toolbar" role="toolbar">
            <section v-if="buttons.length > 1" class="btn-toolbar">
              <div class="btn-group mr-2" role="group">
                <b-button
                  size="sm"
                  v-bind:value="buttonAll.value"
                  v-bind:variant="
                    timeline_type == buttonAll.value ? 'success' : 'light'
                  "
                  v-on:click="$_setTimelineType(buttonAll.value)"
                >
                  {{ $t(buttonAll.text) }}
                </b-button>
              </div>
              <div class="btn-group" role="group">
                <b-button
                  size="sm"
                  v-for="button in buttons"
                  v-bind:key="button.value"
                  v-bind:variant="
                    timeline_type == button.value ? 'success' : 'light'
                  "
                  v-on:click="$_setTimelineType(button.value)"
                >
                  {{ $t(button.text) }}
                </b-button>
              </div>
            </section>
            <section class="update-notification" v-if="updatedCount > 0">
              <div
                class="btn btn-sm btn-warning time-line-updated"
                v-on:click="$_actToCheckUpdate"
              >
                {{
                  $t('appTimeline.message.updateCount', { count: updatedCount })
                }}
              </div>
            </section>
          </nav>
        </transition>
        <div
          class="time-line-wrapper"
          ref="time-line-wrapper"
          v-bind:class="{ 'no-navigation': !isNavigationBarRequired }"
          v-on:scroll="$_getScrollY"
        >
          <div class="time-line-inner">
            <section
              v-for="event in eventsFiltered"
              v-bind:key="event.id"
              class="items"
            >
              <card-checkin
                v-if="event.timeline_type == TIMELINE_TYPES.checkin"
                v-bind:id="event.reference_table_id"
                v-bind:userId="event.send_user_id"
                v-bind:updateAt="event.update_at"
                v-bind:placeName="event.text_1"
                v-bind:comment="event.text_2"
                v-bind:userName="event.send_user_name"
                v-bind:timelineSubtype="event.timeline_subtype"
                v-bind:baseDate="baseDate"
                v-bind:groups="event.send_user_groups"
                v-on:checkincard-title-click="$_showDetail"
              />
              <card-status
                v-else-if="event.timeline_type == TIMELINE_TYPES.status"
                v-bind:id="event.reference_table_id"
                v-bind:userId="event.send_user_id"
                v-bind:updateAt="event.update_at"
                v-bind:statusName="event.text_1"
                v-bind:comment="event.text_2"
                v-bind:statusTypeColor="event.text_3"
                v-bind:userName="event.send_user_name"
                v-bind:baseDate="baseDate"
                v-bind:groups="event.send_user_groups"
                v-on:statuscard-title-click="$_showDetail"
              />
              <card-report
                v-else-if="event.timeline_type == TIMELINE_TYPES.report"
                v-bind:id="event.reference_table_id"
                v-bind:reportTitle="event.text_1"
                v-bind:reportComment="event.text_2"
                v-bind:placeName="event.text_4"
                v-bind:projectId="event.project_id"
                v-bind:projectName="event.text_3"
                v-bind:projectImageAttachFileType="TIMELINE_TYPES.report"
                v-bind:isChecked="event.reference_table_flg"
                v-bind:attachFilesCount="event.attach_file_num"
                v-bind:reportUserId="event.send_user_id"
                v-bind:reportUserName="event.send_user_name"
                v-bind:updateAt="event.update_at"
                v-bind:baseDate="baseDate"
                v-bind:groups="event.send_user_groups"
                v-on:reportcard-title-click="$_showDetail"
                v-on:reportcard-detail-click="$_showDetail"
              />
              <card-message
                v-else-if="event.timeline_type == TIMELINE_TYPES.message"
                v-bind:id="event.reference_table_id"
                v-bind:content="event.text_2"
                v-bind:schedule-title="event.text_3"
                v-bind:isRead="event.reference_table_flg"
                v-bind:attachFilesCount="event.attach_file_num"
                v-bind:sendUserId="event.send_user_id"
                v-bind:sendUserName="event.send_user_name"
                v-bind:sendAt="event.update_at"
                v-bind:baseDate="baseDate"
                v-bind:groups="event.send_user_groups"
                v-on:messagecard-click="$_showDetail"
              />
            </section>
            <aside class="time-line-append">
              <b-button
                size="sm"
                variant="light"
                v-if="showAppendButton"
                v-on:click="$_updateTimeline(false)"
              >
                {{ $t('appTimeline.message.getNext', { limit: limit }) }}
              </b-button>
            </aside>
          </div>
        </div>
      </article>
    </loading-mask>
    <report-detail v-if="isReportAvailable" ref="report-detail" />
    <message-detail v-if="isMessageAvailable" ref="message-detail" />
    <checkin-detail v-if="isCheckinAvailable" ref="checkin-detail" />
    <status-detail v-if="isStatusAvailable" ref="status-detail" />
  </div>
</template>

<script>
import moment from 'moment'
import { inject } from 'vue'
import ButtonReload from '@/components/AppButtonReload.vue'
import CardReport from '@/components/report/AppCardReport.vue'
import CardMessage from '@/components/message/AppCardMessage.vue'
import CardCheckin from '@/components/checkin/AppCardCheckin.vue'
import CardStatus from '@/components/status/AppCardStatus.vue'
import ReportDetail from '@/views/TheReportDetail.vue'
import MessageDetail from '@/views/TheMessageDetail.vue'
import CheckinDetail from '@/views/TheCheckinDetail.vue'
import StatusDetail from '@/views/TheStatusDetail.vue'
import {
  FUNCTION_TYPE,
  MAX_SAFE_INTERVAL_DELAY,
  DATE_FORMAT_HYPHEN,
  TIME_FORMAT_MILLISECOND,
} from '@/constants.js'
import { RestApiClient } from 'ifield-core-components'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { toLocalTz, toServerTz } from '@/js/TimezoneHelper'

// ISOフォーマット(ミリ秒)
const isoFormat = `${DATE_FORMAT_HYPHEN}T${TIME_FORMAT_MILLISECOND}`

// スクロールのあるべき位置のenum
const SCROLL_POSITION_SHOULD_BE = {
  top: 1,
  keepY: 2,
  keepContent: 3,
}

// タイムライン種類
const TIMELINE_TYPES = {
  all: '0',
  status: FUNCTION_TYPE.STATUS,
  message: FUNCTION_TYPE.SEND_MESSAGE,
  checkin: FUNCTION_TYPE.CHECKIN,
  report: FUNCTION_TYPE.REPORT,
}

// ボタン定義
const TIMELINE_TYPE_BUTTONS = [
  { value: TIMELINE_TYPES.all, text: 'appTimeline.filter.all' },
  { value: TIMELINE_TYPES.report, text: 'appTimeline.filter.report' },
  { value: TIMELINE_TYPES.status, text: 'appTimeline.filter.status' },
  { value: TIMELINE_TYPES.checkin, text: 'appTimeline.filter.checkin' },
  { value: TIMELINE_TYPES.message, text: 'appTimeline.filter.message' },
]

export default {
  name: 'AppTimeline',
  components: {
    'button-reload': ButtonReload,
    'card-report': CardReport,
    'card-message': CardMessage,
    'card-checkin': CardCheckin,
    'card-status': CardStatus,
    'report-detail': ReportDetail,
    'message-detail': MessageDetail,
    'checkin-detail': CheckinDetail,
    'status-detail': StatusDetail,
  },
  props: {
    // 1回の読み込み件数
    limit: {
      type: Number,
      default: 10,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    const baseDate = inject('baseDate')
    return { handleApiError, baseDate }
  },
  data: function () {
    return {
      // タイムラインイベント
      events: [],
      // 報告書プロジェクト
      reportProjects: [],
      // タイムライン種類(0は指定なし)
      timeline_type: TIMELINE_TYPES.all,
      // 更新された件数
      updatedCount: 0,
      // 最終更新日時
      lastUpdated: null,
      // 自動更新タイマーID
      intervalId: undefined,
      // 次取得ボタン表示フラグ
      showAppendButton: true,
      // スクロールエレメント
      scrollElement: undefined,
      // スクロール位置(Y方向)
      scrollY: 0,
      // 更新前のスクロール内高さ
      lastScrollHeight: 0,
      // スクロールのあるべき位置
      scrollPositionShouldBe: 0,
      // タイムラインタイプ定義
      TIMELINE_TYPES: TIMELINE_TYPES,
      // ロード中の場合はtrue
      isLoading: false,
    }
  },
  computed: {
    // 更新頻度(ms)
    interval: function () {
      const interval = this.$store.state.timeline.updateInterval * 1000
      return interval > MAX_SAFE_INTERVAL_DELAY
        ? MAX_SAFE_INTERVAL_DELAY
        : interval
    },
    // ボタン(全選択用)
    buttonAll: function () {
      return TIMELINE_TYPE_BUTTONS[0]
    },
    // ボタン(絞り込み用)
    buttons: function () {
      return TIMELINE_TYPE_BUTTONS.slice(1).filter((button) => {
        if (button.value === TIMELINE_TYPES.report) {
          return this.isReportAvailable
        } else if (button.value === TIMELINE_TYPES.status) {
          return this.isStatusAvailable
        } else if (button.value === TIMELINE_TYPES.checkin) {
          return this.isCheckinAvailable
        } else if (button.value === TIMELINE_TYPES.message) {
          return this.isMessageAvailable
        }
      })
    },
    // 補完したevents
    eventsFixed: function () {
      return this.events.map((event) => {
        // 添付ファイル数を数値に変換
        event.attach_file_num = +event.attach_file_num

        // 報告書カード
        if (event.timeline_type == TIMELINE_TYPES.report) {
          // プロジェクト名をプロジェクトIDに変換
          let matchedProject = this.reportProjects.find(
            (prj) => prj.report_project_name === event.text_3
          )
          event.project_id = matchedProject ? matchedProject.id : null
        }

        return event
      })
    },
    // 絞り込みevents
    eventsFiltered: function () {
      if (this.timeline_type === TIMELINE_TYPES.all) {
        return this.eventsFixed
      } else {
        return this.eventsFixed.filter((event) => {
          return event.timeline_type == this.timeline_type
        })
      }
    },
    // 表示用更新時刻
    timeLastUpdated: function () {
      if (!this.lastUpdated) return ''
      return moment(this.lastUpdated).format(
        this.$t('appTimeline.listUpdate.format')
      )
    },
    /**
     * ステイタスは利用可能か？
     * @type {Boolean}
     */
    isStatusAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.STATUS)
    },
    /**
     * メッセージは利用可能か？
     * @type {Boolean}
     */
    isMessageAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.SEND_MESSAGE)
    },
    /**
     * チェックインは利用可能か？
     * @type {Boolean}
     */
    isCheckinAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.CHECKIN)
    },
    /**
     * 報告書は利用可能か？
     * @type {Boolean}
     */
    isReportAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.REPORT)
    },
    /**
     * ナビゲーションバーが必要な場合はtrue
     * @type {Boolean}
     */
    isNavigationBarRequired: function () {
      return this.buttons.length > 1 || this.updatedCount > 0
    },
    /**
     * エラーコード
     * @type {Number}
     */
    errorCode: function () {
      return this.$store.state.error.code
    },
  },
  created: function () {},
  updated: function () {
    this.$_moveScroll()
  },
  mounted: function () {
    // 報告書プロジェクト情報取得
    this.isReportAvailable && this.$_getReportProjects()
    // タイムライン更新
    this.$_updateTimeline()
    // 自動更新設定
    this.$_startAutoUpdateTimeline()
    // スクロールの出るエレメントを取得
    this.$nextTick(() => {
      this.scrollElement = this.$refs['time-line-wrapper']
    })
  },
  beforeDestroy: function () {
    // インターバル処理を殺す
    this.$_stopAutoUpdateTimeline()
  },
  methods: {
    /**
     * 報告書プロジェクトを取得する
     */
    $_getReportProjects: function () {
      let url = RestApiClient.createApiUrl('report_project')
      RestApiClient.executeGetApi(url)
        .then((json) => {
          this.reportProjects = json.data
        })
        .catch((error) => {
          this.handleApiError(error)
        })
    },

    /**
     * タイムライン種類を設定する
     * @param {String} type タイムライン種類
     */
    $_setTimelineType: function (type) {
      this.timeline_type = type
    },

    /**
     * スクロール位置を取得する
     */
    $_getScrollY: function () {
      if (this.scrollElement) {
        this.scrollY = this.scrollElement.scrollTop
      }
    },

    /**
     * 更新を確認する時の処理
     */
    $_actToCheckUpdate: function () {
      this.scrollPositionShouldBe = SCROLL_POSITION_SHOULD_BE.top
      this.updatedCount = 0
    },

    /**
     * タイムラインの自動更新を開始する
     */
    $_startAutoUpdateTimeline: function () {
      this.intervalId = setInterval(this.$_updateTimelineAuto, this.interval)
      this.$log.info('Timeline auto updating has started.')
    },

    /**
     * タイムラインの自動更新を停止する
     */
    $_stopAutoUpdateTimeline: function () {
      clearInterval(this.intervalId)
      this.intervalId = null
      this.$log.info('Timeline auto updating has stopped.')
    },

    /**
     * 自動タイムライン更新処理ラッパー
     */
    $_updateTimelineAuto: function () {
      this.$_updateTimeline(true, false)
      this.$log.info('Timeline auto updated')
    },

    /**
     * タイムライン更新
     * @param {boolean} [getLatest = true]  最新イベントを取得するか？
     * @param {Boolean} [showLoading = true] ローディングを表示するか？
     */
    $_updateTimeline: async function (getLatest = true, showLoading = true) {
      if (showLoading) {
        this.$_showLoading()
      }
      // スクロールのあるべき位置更新
      this.scrollPositionShouldBe = this.$_getScrollPositionShouldBe(getLatest)
      // 更新前のスクロール位置取得
      this.$_setLastScrollHeight()
      // 更新
      let updateTime = new Date()
      try {
        const data = await this.$_getTimeline(getLatest)
        this.$_appendTimeline(data, getLatest)
      } catch {
        this.$_hideLoading()
      }
      this.$_refreshLastUpdated(getLatest, updateTime)
      this.$_hideLoading()
    },

    /**
     * スクロールのあるべき位置を取得する
     * @param {Boolean} getLatest 取得するのは最新イベントか？
     * @return {Number} スクロールのあるべき位置
     */
    $_getScrollPositionShouldBe: function (getLatest) {
      if (this.events.length === 0) {
        return SCROLL_POSITION_SHOULD_BE.top
      }
      if (getLatest) {
        return SCROLL_POSITION_SHOULD_BE.keepContent
      }
      return SCROLL_POSITION_SHOULD_BE.keepY
    },

    /**
     * タイムライン取得
     * @param {boolean} getLatest  取得するのは最新イベントか？
     */
    $_getTimeline: async function (getLatest) {
      // APIのURLを作成する
      let url = RestApiClient.createApiUrl('timeline')

      // パラメータ設定
      let params = url.searchParams
      if (getLatest) {
        // 更新時
        if (this.lastUpdated == null) {
          //初期取得時
          params.append('limit', this.limit)
        } else {
          params.append(
            'after',
            toServerTz(+moment(this.lastUpdated), isoFormat)
          )
        }
      } else {
        // 次を取得時
        const before = this.events.slice(-1)[0].update_at
        params.append('before', toServerTz(before, isoFormat))
        params.append('limit', this.limit)
      }

      // APIを叩く
      try {
        const json = await RestApiClient.executeGetApi(url)
        return json.data.map((d) => ({
          ...d,
          update_at: toLocalTz(d.update_at, isoFormat),
        }))
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * タイムラインを追加する
     * @param {Object[]} data 取得したdata
     * @param {boolean} getLatest  取得したのは最新イベントか？
     */
    $_appendTimeline: function (data, getLatest) {
      if (this.events.length == 0) {
        // 空の場合は代入
        this.events = data
      } else if (getLatest) {
        // 新しいイベントを取得した場合
        this.events = data.concat(this.events)
        this.updatedCount += data.length
      } else {
        // 古いイベントを取得した場合
        this.events = this.events.concat(data)
        // 取得件数がlimit未満なら次を取得ボタンのフラグを変える
        if (data.length < this.limit) {
          this.showAppendButton = false
        }
      }
    },

    /**
     * 最終更新時刻を更新する
     * @param {boolean} getLatest  取得したのは最新イベントか？
     * @param {Date} updateTime 更新時刻
     */
    $_refreshLastUpdated: function (getLatest, updateTime) {
      if (getLatest) {
        this.lastUpdated = updateTime
      }
    },

    /**
     * 更新前のスクロール高さを取得する
     */
    $_setLastScrollHeight: function () {
      if (this.scrollElement) {
        this.lastScrollHeight = this.scrollElement.scrollHeight
      }
    },

    /**
     * スクロールを移動する
     */
    $_moveScroll: function () {
      // スクロールが取得できない時は何もしない
      if (!this.scrollElement) {
        return
      }

      let y = this.scrollY
      switch (this.scrollPositionShouldBe) {
        case SCROLL_POSITION_SHOULD_BE.top:
          y = 0
          break
        case SCROLL_POSITION_SHOULD_BE.keepY:
          y = this.scrollY
          break
        case SCROLL_POSITION_SHOULD_BE.keepContent:
          y =
            this.scrollElement.scrollHeight -
            this.lastScrollHeight +
            this.scrollY
          break
        default:
        // 何もしない
      }
      this.scrollElement.scroll(0, y)
    },

    /**
     * 詳細画面を表示する
     * @param {String} timelineType タイムライン種類
     * @param {Number} id ID
     */
    $_showDetail: function (timelineType, id) {
      if (timelineType == TIMELINE_TYPES.status) {
        this.$refs['status-detail'].showModal(id)
      } else if (timelineType == TIMELINE_TYPES.message) {
        this.$refs['message-detail'].showModal(id)
      } else if (timelineType == TIMELINE_TYPES.checkin) {
        this.$refs['checkin-detail'].showModal(id)
      } else if (timelineType == TIMELINE_TYPES.report) {
        this.$refs['report-detail'].showModal(id)
      }
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
  watch: {
    errorCode: {
      async handler(val) {
        if (val === 401) {
          this.$_stopAutoUpdateTimeline()
        }
      },
      immediate: true,
    },
    interval: function () {
      this.$_stopAutoUpdateTimeline()
      this.$_startAutoUpdateTimeline()
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../assets/scss/parts/title-bar';

$-title-bar-height: 3.2rem;
$-toolbar-height: 1.8rem;
$-content-wrapper-margin-bottom: 20px;

.box {
  height: 100%;
  background-color: #ffffff;
  border: 1px solid #ebebeb;
  border-radius: 4px;
}

.title-bar {
  height: $-title-bar-height;
  display: flex;
  justify-content: space-between;

  h1 {
    font-size: $font-size-middle;
    letter-spacing: 0;
  }
}

.box-content {
  padding: 20px;
  height: calc(100% - #{$-title-bar-height});
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: $-toolbar-height;
  margin-bottom: $-content-wrapper-margin-bottom;
}

.btn-toolbar {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.btn-group .btn {
  font-size: 0.8em;

  &.btn-light {
    background-color: #f4f5f6;

    &:hover {
      background-color: #e0e1e2;
    }
  }

  &.btn-success {
    background-color: #2a8e5c;
  }
}

.update-notification {
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.time-line-updated {
  padding: 2px 15px;
  background: #ffdd9c;
  white-space: nowrap;
  cursor: pointer;
}

.time-line-wrapper {
  height: calc(100% - #{$-toolbar-height} - #{$-content-wrapper-margin-bottom});
  margin-bottom: $-content-wrapper-margin-bottom;
  flex-grow: 1;
  flex-basis: 0;
  overflow-y: scroll;
  @include mixin-scrollbar;

  &.no-navigation {
    height: 100%;
  }
}

.time-line-inner {
  height: auto;

  .items {
    width: 100%;
    margin-bottom: 20px;
    background-color: #ffffff;
  }
}

.time-line-append {
  margin-bottom: 10px;
}

@mixin animation($second) {
  -webkit-transition: all $second;
  -moz-transition: all $second;
  -ms-transition: all $second;
  -o-transition: all $second;
  transition: all $second;
}

.expand-enter-active,
.expand-leave-active {
  @include animation(0.2s);
}
.expand-enter,
.expand-leave-to {
  height: 0;
  margin: 0;
  opacity: 0;
}
</style>
